import React, { FunctionComponent } from "react";
import { Checkbox, DatePicker, Input, InputNumber, Radio, Select } from "antd";
import moment from "moment";
import { V2Question, V2QuestionOption } from "../../helper";
import { QUESTION_TYPES } from "../../constants";
import { getSessionFeedbackNumberRange } from "../../sessionFeedbackNumberRange";
import { CellValue } from "../types";
import StarRatingLarge from "./StarRatingLarge";
import styles from "../TabularForm.module.scss";

const DATE_STORE = "YYYY-MM-DD";
const DATETIME_STORE = "YYYY-MM-DD HH:mm:ss";
const DATE_DISPLAY = "DD/MM/YYYY";
const DATETIME_DISPLAY = "DD/MM/YYYY HH:mm";

export interface CellRendererProps {
  question: V2Question;
  value: CellValue;
  onChange: (value: CellValue) => void;
  disabled?: boolean;
  formType?: string;
  // Pre-migration form id; drives legacy SESSION_FEEDBACK /10 scoring.
  legacyId?: string | null;
}

const options = (question: V2Question): V2QuestionOption[] => question.options || [];

// Renders the editor for a question type; value is already normalised for the v2 payload.
const CellRenderer: FunctionComponent<CellRendererProps> = ({ question, value, onChange, disabled, formType, legacyId }) => {
  switch (question.questionType) {
    case QUESTION_TYPES.SHORT_TEXT:
      // Auto-sizing textarea so long answers wrap. Enter moves down; Shift+Enter adds a line.
      return (
        <Input.TextArea
          className={styles.cellTextarea}
          bordered={false}
          autoSize={{ minRows: 1, maxRows: 4 }}
          placeholder={question.placeholder || ""}
          value={(value as string) || ""}
          onChange={event => onChange(event.target.value)}
          disabled={disabled}
        />
      );

    case QUESTION_TYPES.LONG_TEXT:
      return (
        <Input.TextArea
          className={styles.cellTextarea}
          bordered={false}
          autoSize={{ minRows: 2, maxRows: 6 }}
          placeholder={question.placeholder || ""}
          value={(value as string) || ""}
          onChange={event => onChange(event.target.value)}
          disabled={disabled}
        />
      );

    case QUESTION_TYPES.NUMBER: {
      // SESSION_FEEDBACK bounds match the personalized form, overriding the question's own validation.
      const isSessionFeedback = formType === "SESSION_FEEDBACK";
      const { min, max } = isSessionFeedback
        ? getSessionFeedbackNumberRange(legacyId)
        : { min: question.validation?.minValue, max: question.validation?.maxValue };
      return (
        <div className={styles.numberCell}>
          <InputNumber
            className={styles.cellNumber}
            bordered={false}
            controls={false}
            placeholder={question.placeholder || ""}
            value={typeof value === "number" ? value : (value as never)}
            min={min}
            max={max}
            onChange={next => onChange((next as number) ?? null)}
            disabled={disabled}
          />
          {typeof max === "number" && <span className={styles.numberOutOf}>/ {max}</span>}
        </div>
      );
    }

    case QUESTION_TYPES.RATING:
      return <StarRatingLarge question={question} value={typeof value === "number" ? value : null} onChange={onChange} disabled={disabled} />;

    case QUESTION_TYPES.DROPDOWN:
      return (
        <Select
          className={styles.cellSelect}
          bordered={false}
          showSearch
          allowClear
          optionFilterProp="label"
          placeholder="Select"
          value={(value as string) || undefined}
          onChange={next => onChange((next as string) ?? null)}
          options={options(question).map(option => ({ label: option.label, value: option.value }))}
          disabled={disabled}
          getPopupContainer={trigger => trigger.parentElement || document.body}
        />
      );

    case QUESTION_TYPES.MULTISELECT:
      return (
        <Select
          className={styles.cellSelect}
          bordered={false}
          mode="multiple"
          allowClear
          maxTagCount="responsive"
          optionFilterProp="label"
          placeholder="Select"
          value={(value as string[]) || []}
          onChange={next => onChange((next as string[]) || null)}
          options={options(question).map(option => ({ label: option.label, value: option.value }))}
          disabled={disabled}
          getPopupContainer={trigger => trigger.parentElement || document.body}
        />
      );

    case QUESTION_TYPES.CHECKBOX:
      return (
        <Checkbox.Group
          className={styles.cellChoiceGroup}
          value={(value as string[]) || []}
          onChange={next => onChange((next as string[]) || null)}
          disabled={disabled}
        >
          {options(question).map(option => (
            <Checkbox key={option.value} value={option.value} className={styles.choiceItem}>
              {option.label}
            </Checkbox>
          ))}
        </Checkbox.Group>
      );

    case QUESTION_TYPES.MULTI_CHOICE:
      return (
        <Radio.Group
          className={styles.cellChoiceGroup}
          value={(value as string) || undefined}
          onChange={event => onChange(event.target.value)}
          disabled={disabled}
        >
          {options(question).map(option => (
            // antd Radio.Group can't clear a selection; clicking the selected option clears it.
            <Radio
              key={option.value}
              value={option.value}
              className={styles.choiceItem}
              onClick={() => {
                if (!disabled && value === option.value) onChange(null);
              }}
            >
              {option.label}
            </Radio>
          ))}
        </Radio.Group>
      );

    case QUESTION_TYPES.DATE_PICKER:
      return (
        <DatePicker
          className={styles.cellDate}
          bordered={false}
          format={DATE_DISPLAY}
          placeholder={DATE_DISPLAY}
          value={value ? moment(value as string, DATE_STORE) : null}
          onChange={next => onChange(next ? next.format(DATE_STORE) : null)}
          disabled={disabled}
          getPopupContainer={trigger => trigger.parentElement || document.body}
        />
      );

    case QUESTION_TYPES.DATE_TIME_PICKER:
      return (
        <DatePicker
          className={styles.cellDate}
          bordered={false}
          showTime
          format={DATETIME_DISPLAY}
          placeholder={DATETIME_DISPLAY}
          value={value ? moment(value as string, DATETIME_STORE) : null}
          onChange={next => onChange(next ? next.format(DATETIME_STORE) : null)}
          disabled={disabled}
          getPopupContainer={trigger => trigger.parentElement || document.body}
        />
      );

    default:
      // Unsupported types (e.g. FILE_UPLOAD) render nothing editable.
      return <span className={styles.cellPlaceholder}>—</span>;
  }
};

export default CellRenderer;
